import { type MetaFunction } from '@remix-run/node'
import { EndCap } from './components/end-cap.tsx'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'
import { MarketingSection } from './components/marketing-section.tsx'

const FAQS: Array<{ q: string; a: React.ReactNode }> = [
	{
		q: 'How do I sign up as a coach?',
		a: 'Click Sign up at the top of any page. Choose "Coach" during onboarding to enable team and roster features.',
	},
	{
		q: 'Can I bring my existing athletes over?',
		a: 'Yes. Coaches can invite athletes by email after signing up; bulk import for full rosters is supported.',
	},
	{
		q: 'What does the workout notation look like?',
		a: (
			<>
				A workout is a sequence of sections; each section is an exercise or a
				circuit. See the{' '}
				<a className="ember-link" href="/how-to">
					grammar walkthrough
				</a>{' '}
				for live examples.
			</>
		),
	},
	{
		q: 'Where does my data live?',
		a: 'Momentum stores training data on our infrastructure. You can export your data and delete your account at any time from Settings.',
	},
	{
		q: 'How do I delete my account?',
		a: 'Go to Settings → Profile and use Delete Account. Deletion removes your training history and any coaching relationships you initiated.',
	},
	{
		q: 'I found a bug. Where do I report it?',
		a: (
			<>
				Email{' '}
				<a className="ember-link" href="mailto:hello@momentum.fitness">
					hello@momentum.fitness
				</a>
				. Include the page URL and a screenshot if possible.
			</>
		),
	},
]

export const meta: MetaFunction = () => [
	{ title: 'Support — Momentum' },
	{
		name: 'description',
		content: 'Help, fast. Common answers and direct contact.',
	},
]

export default function SupportRoute() {
	return (
		<>
			<MarketingHero
				eyebrow="Support"
				heading="Help, fast."
				subhead="Common answers below. Otherwise, write to us."
				ctaLabel=""
				variant="compact"
			/>

			<MarketingSection>
				<EyebrowLabel>Common questions</EyebrowLabel>
				<div className="mt-10 divide-y divide-m-rule border-y border-m-rule">
					{FAQS.map((item) => (
						<details key={item.q} className="group py-5">
							<summary className="flex cursor-pointer list-none items-center justify-between text-base font-medium text-ink-fg">
								<span>{item.q}</span>
								<span
									aria-hidden
									className="text-ember transition-transform group-open:rotate-45"
								>
									+
								</span>
							</summary>
							<div className="mt-4 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
								{item.a}
							</div>
						</details>
					))}
				</div>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>Direct line</EyebrowLabel>
				<p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Write to{' '}
					<a className="ember-link" href="mailto:hello@momentum.fitness">
						hello@momentum.fitness
					</a>
					. We aim to respond within one business day.
				</p>
			</MarketingSection>

			<EndCap />
		</>
	)
}
